/* Sliding cart: open from any [data-os-cart-open], change quantities, and
   re-render from the Section Rendering API so Liquid stays the single source
   of truth for prices and discounts - no price maths in JS. */
(() => {
  const root = () => document.querySelector('[data-os-cart]');
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const pageStatus = document.createElement('span');
  pageStatus.className = 'visually-hidden';
  pageStatus.setAttribute('role', 'status');
  pageStatus.setAttribute('aria-live', 'polite');
  pageStatus.setAttribute('aria-atomic', 'true');
  pageStatus.dataset.osPageCartStatus = '';
  document.body.appendChild(pageStatus);
  let statusTimer;
  const announce = (text) => {
    clearTimeout(statusTimer);
    [pageStatus, root()?.querySelector('[data-os-cart-status]')].forEach((node) => {
      if (node?.textContent === text) node.textContent = '';
    });
    statusTimer = setTimeout(() => {
      const cart = root();
      const target = cart?.classList.contains('is-open')
        ? cart.querySelector('[data-os-cart-status]') : pageStatus;
      if (target) target.textContent = text;
    }, 450);
  };
  const announceError = (message) => {
    if (!root()?.classList.contains('is-open') && document.activeElement?.closest('[data-os-pdp]')) return;
    announce(message || 'Something went wrong');
  };
  // The sliding panel carries the drawer's transition; the root only toggles hidden.
  const PANEL = '.os-cart__panel';
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let pageScroll;
  const lockPageScroll = () => {
    if (pageScroll) return;
    pageScroll = { x: window.scrollX, y: window.scrollY };
    document.body.style.setProperty('--os-cart-page-top', `${-pageScroll.y}px`);
    document.body.style.setProperty('--os-cart-page-left', `${-pageScroll.x}px`);
    document.documentElement.classList.add('is-os-cart-open');
    document.body.classList.add('is-os-cart-open');
  };
  const unlockPageScroll = () => {
    if (!pageScroll) return;
    const { x, y } = pageScroll;
    pageScroll = null;
    document.documentElement.classList.remove('is-os-cart-open');
    document.body.classList.remove('is-os-cart-open');
    document.body.style.removeProperty('--os-cart-page-top');
    document.body.style.removeProperty('--os-cart-page-left');
    window.scrollTo({ left: x, top: y, behavior: 'instant' });
  };

  let returnFocus;
  const backgroundInertState = new Map();

  const focusables = (scope) => [...scope.querySelectorAll(FOCUSABLE)].filter((el) => (
    el.tabIndex >= 0
    && !el.closest('[hidden], [inert]')
    && getComputedStyle(el).display !== 'none'
    && getComputedStyle(el).visibility !== 'hidden'
  ));
  const focusWithoutScroll = (el) => el?.focus({ preventScroll: true });
  const focusFirst = (el) => focusWithoutScroll(focusables(el.querySelector(PANEL) || el)[0]);

  // Inert every sibling along the drawer's ancestor chain. This covers the
  // footer-group wrapper as well as the other top-level page regions, while
  // remembering any inert state the page already owned before the cart opened.
  const makeBackgroundInert = (el) => {
    let branch = el;
    while (branch && branch !== document.body) {
      const parent = branch.parentElement;
      if (!parent) break;
      [...parent.children].forEach((sibling) => {
        if (sibling === branch || backgroundInertState.has(sibling)) return;
        backgroundInertState.set(sibling, sibling.hasAttribute('inert'));
        sibling.setAttribute('inert', '');
      });
      branch = parent;
    }
  };
  const restoreBackground = () => {
    backgroundInertState.forEach((wasInert, el) => {
      if (el.isConnected && !wasInert) el.removeAttribute('inert');
    });
    backgroundInertState.clear();
  };

  // Cart section re-renders replace the focused node. Capture a stable identity
  // before the swap, then restore its equivalent control in the fresh markup.
  const focusIdentity = (el, cart) => {
    if (!(el instanceof HTMLElement) || !cart.contains(el)) return null;
    const controls = focusables(cart);
    const lineKey = el.closest('[data-os-line-key]')?.dataset.osLineKey;
    if (lineKey && el.hasAttribute('data-os-qty-step')) {
      return { kind: 'line-step', lineKey, value: el.dataset.osQtyStep, index: controls.indexOf(el) };
    }
    if (lineKey && el.hasAttribute('data-os-line-sub')) return { kind: 'line-sub', lineKey, index: controls.indexOf(el) };
    if (lineKey && el.hasAttribute('data-os-line-freq')) return { kind: 'line-freq', lineKey, index: controls.indexOf(el) };
    if (el.hasAttribute('data-os-cart-add')) return { kind: 'add', value: el.dataset.osCartAdd, index: controls.indexOf(el) };
    if (el.hasAttribute('data-os-up-prev')) return { kind: 'up-prev', index: controls.indexOf(el) };
    if (el.hasAttribute('data-os-up-next')) return { kind: 'up-next', index: controls.indexOf(el) };
    if (el.matches('.os-cart__checkout')) return { kind: 'checkout', index: controls.indexOf(el) };
    if (el.matches('.os-cart__close')) return { kind: 'close', index: controls.indexOf(el) };
    return { kind: 'index', index: controls.indexOf(el) };
  };
  const focusFromIdentity = (cart, identity) => {
    if (!identity) return focusFirst(cart);
    const lines = [...cart.querySelectorAll('[data-os-line-key]')];
    const line = lines.find((item) => item.dataset.osLineKey === identity.lineKey);
    let target;
    if (identity.kind === 'line-step') {
      target = [...(line?.querySelectorAll('[data-os-qty-step]') || [])]
        .find((el) => el.dataset.osQtyStep === identity.value);
    } else if (identity.kind === 'line-sub') target = line?.querySelector('[data-os-line-sub]');
    else if (identity.kind === 'line-freq') target = line?.querySelector('[data-os-line-freq]');
    else if (identity.kind === 'add') {
      target = [...cart.querySelectorAll('[data-os-cart-add]')]
        .find((el) => el.dataset.osCartAdd === identity.value);
    } else if (identity.kind === 'up-prev') target = cart.querySelector('[data-os-up-prev]');
    else if (identity.kind === 'up-next') target = cart.querySelector('[data-os-up-next]');
    else if (identity.kind === 'checkout') target = cart.querySelector('.os-cart__checkout');
    else if (identity.kind === 'close') target = cart.querySelector('.os-cart__close');
    const controls = focusables(cart);
    if (target && !controls.includes(target)) target = null;
    if (!target && identity.index >= 0) target = controls[Math.min(identity.index, controls.length - 1)];
    focusWithoutScroll(target || controls[0]);
  };

  const trapFocus = (e, cart) => {
    const controls = focusables(cart.querySelector(PANEL) || cart);
    if (!controls.length) { e.preventDefault(); return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    const active = document.activeElement;
    if (!controls.includes(active)) {
      e.preventDefault();
      focusWithoutScroll(e.shiftKey ? last : first);
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      focusWithoutScroll(last);
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      focusWithoutScroll(first);
    }
  };

  // Longest transition-duration an element declares, in ms (0 when it has none).
  const transitionMs = (el) => Math.max(0, ...getComputedStyle(el).transitionDuration
    .split(',')
    .map((d) => (parseFloat(d) || 0) * (d.trim().endsWith('ms') ? 1 : 1000)));

  // close() hides the drawer once its slide-out transition has ended; an
  // open() inside that window must cancel the pending hide or the drawer ends
  // up hidden while carrying is-open. The transitionend listener and the
  // fallback timer are cancelled together.
  let hideTimer;
  let hideListener;
  const cancelHide = () => {
    clearTimeout(hideTimer);
    hideListener?.abort();
    hideListener = null;
  };
  const open = (opener) => {
    const el = root(); if (!el) return;
    const wasOpen = el.classList.contains('is-open') && !el.hidden;
    if (!wasOpen) {
      const candidate = opener instanceof HTMLElement ? opener : document.activeElement;
      returnFocus = candidate instanceof HTMLElement && candidate !== document.body && !el.contains(candidate)
        ? candidate
        : null;
      makeBackgroundInert(el);
    }
    cancelHide();
    el.hidden = false;
    // Force a reflow so the transition runs from the closed state. rAF is not
    // reliable here: background tabs throttle it, and callers may open
    // synchronously right after a refresh().
    void el.offsetWidth;
    el.classList.add('is-open');
    lockPageScroll();
    if (!wasOpen || !el.contains(document.activeElement)) focusFirst(el);
  };
  const close = () => {
    const el = root(); if (!el) return;
    el.classList.remove('is-open');
    unlockPageScroll();
    const opener = returnFocus;
    returnFocus = null;
    restoreBackground();
    const fallback = document.querySelector('[data-os-cart-open]');
    if (opener?.isConnected) focusWithoutScroll(opener);
    if (document.activeElement !== opener && fallback instanceof HTMLElement) focusWithoutScroll(fallback);
    cancelHide();
    const panel = el.querySelector(PANEL) || el;
    const hide = () => { cancelHide(); el.hidden = true; };
    hideListener = new AbortController();
    el.addEventListener('transitionend', (e) => {
      if (e.target === panel) hide();
    }, { signal: hideListener.signal });
    // A missing transition (reduced motion) or a panel repainted mid-slide
    // never fires transitionend, so a timer read from the stylesheet backs it up.
    hideTimer = setTimeout(hide, transitionMs(panel) + 50);
  };

  const sectionId = () => root()?.closest('[id^="shopify-section"]')?.id.replace('shopify-section-', '');

  // Swap the drawer's contents for freshly rendered section HTML.
  function paint(html) {
    const el = root(); if (!el || !html) return false;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-os-cart]');
    if (!fresh) return false;
    const wasOpen = el.classList.contains('is-open');
    const identity = wasOpen ? focusIdentity(document.activeElement, el) : null;
    const status = el.querySelector('[data-os-cart-status]');
    el.innerHTML = fresh.innerHTML;
    if (status) el.querySelector('[data-os-cart-status]')?.replaceWith(status);
    // The count lives on the root, which innerHTML does not replace; postCart
    // reads it to paint the badge, so it has to follow every repaint.
    el.dataset.osItemCount = fresh.dataset.osItemCount;
    restorePendingQuantities();
    if (wasOpen) {
      el.classList.add('is-open');
      focusFromIdentity(el, identity);
    }
    return true;
  }
  const paintBadge = (count) => document.querySelectorAll('[data-os-cart-count]').forEach((b) => {
    b.textContent = count; b.hidden = count === 0;
  });

  async function refresh() {
    const id = sectionId(); if (!id) return false;
    try {
      const html = await fetch(`${rootUrl()}?section_id=${id}`).then((r) => r.text());
      const painted = paint(html);
      await syncBadge();
      return painted;
    } catch (err) {
      console.warn('[os-cart] refresh failed', err.message);
      return false;
    }
  }

  const getCart = () => fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json());

  async function syncBadge() {
    try {
      paintBadge((await getCart()).item_count);
    } catch (err) {
      console.warn('[os-cart] badge sync failed', err.message);
    }
  }

  // Every cart mutation goes through here. The request asks Shopify to render
  // the drawer section in the same response (`sections`), so one round trip
  // both changes the cart and repaints the drawer. Throws on a non-2xx with
  // Shopify's own message so callers never paint state the cart rejected.
  const addedMessage = (items, requested) => {
    const labels = root()?.querySelector('[data-os-cart-status]')?.dataset;
    if (!labels?.added || !items?.length || !requested?.length) return '';
    const descriptions = requested.map((request) => {
      const item = items.find((line) => String(line.variant_id || line.id) === String(request.id));
      const quantity = Number(request.quantity);
      if (!item || !Number.isInteger(quantity) || quantity < 1) return '';
      const small = Number(item.product_id) === 8777705586871;
      const large = Number(item.product_id) === 8777704079543;
      const flavor = item.variant_title?.split(' - ')[0]?.trim();
      if ((small || large) && flavor) {
        const pack = small ? (quantity === 1 ? labels.smallOne : labels.smallMany)
          : (quantity === 1 ? labels.largeOne : labels.largeMany);
        if (pack) return `${quantity} ${flavor} ${pack}`;
      }
      return item.title ? `${quantity} ${item.title}` : '';
    });
    if (descriptions.some((description) => !description)) return '';
    const last = descriptions.pop();
    const list = descriptions.length ? `${descriptions.join(', ')} ${labels.and} ${last}` : last;
    return labels.added.replace('[items]', list);
  };

  // Include line resolution in the same queue as writes, so another local
  // mutation cannot change discounts or reindex lines between the read/write.
  let mutationChain = Promise.resolve();
  const mutate = (fn) => {
    const result = mutationChain.then(fn);
    mutationChain = result.catch(() => {});
    return result;
  };
  const postCart = (path, body) => mutate(() => postCartNow(path, body));
  async function postCartNow(path, body, beforePaint = () => {}) {
    const requested = path === 'add.js' ? body.items?.map(({ id, quantity }) => ({ id, quantity })) : null;
    const trackingRequested = requested ? body.items.map(({id, quantity, selling_plan, properties}) => ({
      id, quantity, selling_plan, properties: properties ? {...properties} : {},
    })) : null;
    const id = sectionId();
    if (id) body.sections = id;
    const res = await fetch(`${rootUrl()}cart/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).catch((err) => { announceError('Something went wrong'); throw err; });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json()).description || ''; } catch (_) { /* no JSON body */ }
      announceError(detail);
      throw new Error(detail || `cart/${path} returned ${res.status}`);
    }
    const data = await res.json();
    beforePaint();
    if (requested) {
      try { window.OSTracking?.recordAdd(trackingRequested, data); } catch (_) {}
    }
    if (!(id && data.sections && paint(data.sections[id]))) await refresh();
    const added = requested ? addedMessage(data.items, requested) : '';
    if (added) announce(added);
    // change.js answers with the whole cart; add.js only with the added items,
    // so the badge reads the count the freshly painted drawer carries.
    // Anything mirroring cart state (carousel card steppers) resyncs from
    // os:cart-updated, which always carries a full cart.
    const settled = (cart) => {
      document.dispatchEvent(new CustomEvent('os:cart-updated', { detail: cart }));
      const total = typeof cart.total_price === 'number' ? ` Subtotal ${money(cart.total_price)}.` : '';
      if (!added) announce(`Cart updated. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}.${total}`);
    };
    if (typeof data.item_count === 'number') { paintBadge(data.item_count); settled(data); return data; }
    const painted = Number(root()?.dataset.osItemCount);
    if (!Number.isNaN(painted)) {
      paintBadge(painted);
      getCart().then(settled).catch((err) => console.warn('[os-cart] cart read failed', err.message));
      return data;
    }
    const cart = await getCart();
    paintBadge(cart.item_count);
    settled(cart);
    return cart;
  }

  // Formats cents with the shop's money format (theme.liquid sets window.OSMoneyFormat).
  // NUL is the swap sentinel for `.`/`,`: comma-separator formats exchange the two
  // characters, and a direct replace would turn both into the same glyph.
  // shop.money_format may wrap the amount in <span class="money">; the tags are stripped.
  const money = (cents) => {
    const fmt = window.OSMoneyFormat || '${{amount}}';
    const n = cents / 100;
    const withComma = (v, dec) => v.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const NUL = '\u0000';
    const swapped = withComma(n, 2).replace(/,/g, NUL).replace('.', ',').split(NUL).join('.');
    return fmt
      .replace('{{amount_no_decimals_with_comma_separator}}', withComma(n, 0))
      .replace('{{amount_with_comma_separator}}', swapped)
      .replace('{{amount_no_decimals}}', withComma(n, 0))
      .replace('{{amount}}', withComma(n, 2))
      .replace(/<[^>]*>/g, '');
  };
  const priceHtml = (reg, fin) => (reg > fin
    ? `<s>${money(reg)}</s> <strong class="os-cart__price-final">${money(fin)}</strong>`
    : `<strong>${money(fin)}</strong>`);
  // Paint the line total and subtotal from the unit prices already on the page,
  // so the drawer answers the tap before Shopify does (tier changes settle on repaint).
  function paintOptimistic(item, qty) {
    const price = item.querySelector('.os-cart__price');
    if (price) {
      const reg = Number(price.dataset.osLineReg || 0);
      const fin = Number(price.dataset.osLineFin || 0);
      if (fin) price.innerHTML = priceHtml(reg * qty, fin * qty);
    }
    const el = root();
    let regTotal = 0;
    let finTotal = 0;
    let itemCount = 0;
    el.querySelectorAll('[data-os-line]').forEach((li) => {
      const p = li.querySelector('.os-cart__price');
      const n = Number(li.querySelector('.os-cart__count')?.textContent || 0);
      itemCount += n;
      regTotal += Number(p?.dataset.osLineReg || 0) * n;
      finTotal += Number(p?.dataset.osLineFin || 0) * n;
    });
    const val = el.querySelector('.os-cart__row-val');
    if (val) {
      const off = regTotal > finTotal ? Math.floor((regTotal - finTotal) * 100 / regTotal) : 0; // floor, like Liquid
      val.innerHTML = (off ? `<span class="os-cart__off">${off}% OFF</span> ` : '') + priceHtml(regTotal, finTotal);
    }
    const titleCount = el.querySelector('.os-cart__title-count');
    if (titleCount) titleCount.textContent = `(${itemCount})`;
    el.classList.add('is-updating');
  }

  // Discount applications change Shopify keys. Variant + plan + properties
  // identify the intended purchase without mixing subscriptions and one-offs.
  const lineIdentity = (variant, plan, properties) => JSON.stringify([
    String(variant), String(plan || ''),
    // Liquid serializes properties as pairs; Ajax returns an object.
    (Array.isArray(properties) ? properties : Object.entries(properties || {}))
      .sort(([a], [b]) => a.localeCompare(b)),
  ]);
  const itemIdentity = (item) => lineIdentity(item.dataset.osLineVariant,
    item.dataset.osLinePlan, JSON.parse(item.dataset.osLineProperties || '{}'));
  const pendingQuantities = new Map();
  function restorePendingQuantities() {
    root()?.querySelectorAll('[data-os-line]').forEach((item) => {
      const pending = pendingQuantities.get(itemIdentity(item));
      if (!pending) return;
      const count = item.querySelector('.os-cart__count');
      if (count) count.textContent = pending.quantity;
      item.hidden = pending.quantity === 0;
      // A line being removed cannot be incremented until removal settles.
      item.querySelectorAll('[data-os-qty]').forEach((button) => { button.disabled = pending.quantity === 0; });
      paintOptimistic(item, pending.quantity);
    });
  }

  // Burst coalescing, shared with the carousel card steppers through OSCart.
  // Steppers paint the new count immediately and send one request per burst of
  // taps (the last value wins) instead of one request per click: coalesce(key, fn)
  // keeps only the newest fn per key, runs one at a time per key, and a 220 ms
  // pause ends the burst. The returned promise settles once the key is idle,
  // with the outcome of the last request that ran. The slot is always released,
  // so a failed request can never wedge later taps for the session.
  const COALESCE_MS = 220;
  const queue = new Map();
  // Requests go out one at a time (Shopify reindexes lines on removal) and
  // resolve fresh line keys, so a burst on two lines cannot cross.
  let chain = Promise.resolve();
  function coalesce(key, fn) {
    const q = queue.get(key) || { busy: false, waiters: [] };
    q.latest = fn;
    clearTimeout(q.timer);
    q.timer = setTimeout(() => flush(key), COALESCE_MS);
    queue.set(key, q);
    return new Promise((resolve, reject) => q.waiters.push({ resolve, reject }));
  }
  function flush(key) {
    const q = queue.get(key);
    if (!q || q.busy) return;
    q.busy = true;
    const fn = q.latest;
    chain = chain.then(async () => {
      let outcome;
      try {
        outcome = { value: await fn() };
      } catch (err) {
        outcome = { error: err };
      } finally {
        q.busy = false;
      }
      if (q.latest !== fn) { flush(key); return; } // newer taps arrived while this one was in flight
      queue.delete(key);
      q.waiters.forEach((w) => ('error' in outcome ? w.reject(outcome.error) : w.resolve(outcome.value)));
    });
  }

  const setQty = (identity, pending) => mutate(async () => {
    const cart = await getCart();
    const matches = cart.items.filter((item) => lineIdentity(item.variant_id,
      item.selling_plan_allocation?.selling_plan?.id, item.properties) === identity);
    // Never guess a variant/line when Shopify has split it into ambiguous rows.
    if (matches.length !== 1) throw new Error('Your cart changed. Please try again.');
    return postCartNow('change.js', { id: matches[0].key, quantity: pending.quantity }, () => {
      if (pendingQuantities.get(identity) === pending) pendingQuantities.delete(identity);
    });
  });
  const addItems = (items) => postCart('add.js', { items });
  const addVariant = (id, quantity = 1) => postCart('add.js', { items: [{ id, quantity }] });
  // Set a variant's quantity by variant id (cart/change.js accepts it in `id`).
  const changeVariant = (id, quantity) => postCart('change.js', { id: String(id), quantity });
  // An omitted selling_plan keeps the line's plan; only an explicit null removes it.
  const setLinePlan = (line, quantity, plan) => (
    postCart('change.js', { line, quantity, selling_plan: plan ? Number(plan) : null })
  );

  // Per-line subscription controls: the subscribe toggle sets or clears the
  // line's plan, the frequency dropdown swaps it. Both resolve the line once.
  document.addEventListener('change', async (e) => {
    const control = e.target.closest('[data-os-line-sub], [data-os-line-freq]');
    const item = control?.closest('[data-os-line]');
    if (!control || !item) return;
    const isToggle = control.hasAttribute('data-os-line-sub');
    const plan = isToggle ? (control.checked ? control.dataset.osLineSub : null) : control.value;
    const qty = Number(item.querySelector('.os-cart__count')?.textContent || 1);
    try {
      await setLinePlan(Number(item.dataset.osLine), qty, plan);
    } catch (err) {
      console.warn('[os-cart] selling plan not accepted for this line', err.message);
      if (isToggle) control.checked = !control.checked; // put the toggle back
      else await refresh(); // re-render with the plan the cart still holds
    }
  });

  // ---- click handlers, one per control ----
  const onOpen = (e, btn) => { e.preventDefault(); open(btn); };
  const onClose = (e) => { e.preventDefault(); close(); };
  const onUpsellAdd = (e, btn) => {
    e.preventDefault();
    addVariant(Number(btn.dataset.osCartAdd)).catch((err) => console.warn('[os-cart] add failed', err.message));
  };
  const onUpsellScroll = (e, btn) => {
    const track = btn.closest('.os-cart__up-row')?.querySelector('.os-cart__up-track');
    if (!track) return;
    const dir = btn.hasAttribute('data-os-up-next') ? 1 : -1;
    track.scrollBy({ left: dir * (track.clientWidth + 12), behavior: 'smooth' });
  };
  // Drawer stepper. data-os-qty-step gives the direction, painted at once;
  // data-os-qty is the quantity Shopify rendered for a single tap. Within a
  // burst the count on screen has already moved on, so the posted quantity is
  // that count plus the step (identical to data-os-qty on the first tap).
  const onStep = (e, btn) => {
    e.preventDefault();
    const item = btn.closest('[data-os-line]');
    if (!item?.dataset.osLineKey) return;
    const key = itemIdentity(item);
    const countEl = item.querySelector('.os-cart__count');
    const target = Number(btn.dataset.osQty);
    const shown = pendingQuantities.get(key)?.quantity ?? (countEl ? Number(countEl.textContent) : NaN);
    const step = Number(btn.dataset.osQtyStep) || Math.sign(target - shown) || 1;
    const next = Math.max(0, Number.isNaN(shown) ? target : shown + step);
    if (next === shown) return;
    const pending = { quantity: next };
    pendingQuantities.set(key, pending);
    if (countEl) countEl.textContent = next;
    restorePendingQuantities();
    coalesce(key, () => setQty(key, pending))
      .catch(async (err) => {
        if (!pendingQuantities.has(key)) return; // one recovery per coalesced burst
        pendingQuantities.delete(key);
        announceError(err.message);
        console.warn('[os-cart] change failed', err.message);
        await refresh();
      })
      .finally(() => { if (!queue.size) root()?.classList.remove('is-updating'); });
    // Removal should not wait for the tap-burst debounce. An in-flight change
    // still finishes first; its section render preserves the hidden zero row.
    if (next === 0) flush(key);
  };
  // Checkout is a plain link: show the spinner while the browser navigates,
  // and clear it if the visitor comes back via bfcache (pageshow fires again).
  const onCheckout = (e, btn) => {
    if (pendingQuantities.size) {
      e.preventDefault();
      announce('Updating your cart. Please wait.');
      return;
    }
    if (btn.classList.contains('is-loading')) { e.preventDefault(); return; }
    btn.classList.add('is-loading');
  };
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.os-cart__checkout.is-loading').forEach((b) => b.classList.remove('is-loading'));
  });
  const clickHandlers = [
    ['.os-cart__checkout', onCheckout],
    ['[data-os-cart-open]', onOpen],
    ['[data-os-cart-add]', onUpsellAdd],
    ['[data-os-up-prev], [data-os-up-next]', onUpsellScroll],
    ['[data-os-cart-close]', onClose],
    ['[data-os-qty]', onStep],
  ];
  document.addEventListener('click', (e) => {
    for (const [selector, handler] of clickHandlers) {
      const btn = e.target.closest(selector);
      if (btn) { handler(e, btn); return; }
    }
  });
  // Any native add-to-cart form (accessory PDP, Dawn product forms) goes
  // through the drawer instead of landing on /cart.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form || e.defaultPrevented || form.closest('product-form')) return; // Dawn posts its own forms
    // A form with option selects needs variant resolution we do not do here;
    // the native post lands on /cart, which bounces back with the drawer open.
    if (form.querySelector('select[name^="options"]')) return;
    const id = form.querySelector('[name="id"]')?.value;
    if (!id) return;
    e.preventDefault();
    const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    addVariant(Number(id), qty)
      .then(open)
      .catch((err) => console.warn('[os-cart] add failed', err.message))
      .finally(() => { if (btn) btn.disabled = false; });
  });
  // /cart redirects here with ?cart=open (the page cart never shows).
  if (new URLSearchParams(location.search).get('cart') === 'open') {
    const url = new URL(location.href); url.searchParams.delete('cart');
    history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
    document.addEventListener('DOMContentLoaded', open);
    if (document.readyState !== 'loading') open();
  }
  document.addEventListener('keydown', (e) => {
    const el = root();
    if (!el?.classList.contains('is-open')) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') trapFocus(e, el);
  });
  // Public surface for other scripts (bundle builder, carousel cards). Keep it tiny.
  window.OSCart = {
    open, close, refresh, addVariant, addItems, changeVariant, setLinePlan, getCart, coalesce, money,
  };
  document.dispatchEvent(new Event('os:cart-ready'));
})();
